// Animated lucide icons adapted from pqoqubbw/icons (MIT) — https://github.com/pqoqubbw/icons
// Hover on the icon animates it; or pass a ref and call startAnimation()/stopAnimation()
// from a parent (e.g. button onMouseEnter) to trigger from a larger hover target.
import React, { forwardRef, useCallback, useImperativeHandle, useRef } from "react";
import { Bot } from "lucide-react";
import { motion, useAnimation, useReducedMotion } from "framer-motion";

import type { AnimationControls } from "framer-motion";

/** What every icon here exposes on its ref, so a parent (usually a button with
    a bigger hover target) can drive the animation. */
export interface AnimatedIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

/** The wrapper is a plain div: everything a div takes, plus the two dials. */
export type AnimatedIconProps = React.ComponentPropsWithoutRef<"div"> & {
  size?: number;
  strokeWidth?: number;
};

/** The svg attributes every icon shares. Spelled out rather than typed as
    `SVGProps` so spreading it into `motion.svg` stays a nine-key object. */
interface IconSvgProps {
  fill: string;
  height: number;
  width: number;
  stroke: string;
  strokeLinecap: "round";
  strokeLinejoin: "round";
  strokeWidth: number;
  viewBox: string;
  xmlns: string;
}

function makeIcon(renderSvg: (controls: AnimationControls, svgProps: IconSvgProps) => React.ReactNode) {
  return forwardRef<AnimatedIconHandle, AnimatedIconProps>(({ onMouseEnter, onMouseLeave, className, size = 14, strokeWidth = 1.75, ...props }, ref) => {
    const controls = useAnimation();
    const isControlledRef = useRef(false);

    useImperativeHandle(ref, () => {
      isControlledRef.current = true;
      return {
        startAnimation: () => controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    const enter = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => (isControlledRef.current ? onMouseEnter?.(e) : controls.start("animate")),
      [controls, onMouseEnter]
    );
    const leave = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => (isControlledRef.current ? onMouseLeave?.(e) : controls.start("normal")),
      [controls, onMouseLeave]
    );

    const svgProps: IconSvgProps = {
      fill: "none",
      height: size,
      width: size,
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth,
      viewBox: "0 0 24 24",
      xmlns: "http://www.w3.org/2000/svg",
    };

    return (
      <div
        className={`inline-flex items-center justify-center ${className || ""}`}
        onMouseEnter={enter}
        onMouseLeave={leave}
        {...props}
      >
        {renderSvg(controls, svgProps)}
      </div>
    );
  });
}

export const PlusIcon = makeIcon((controls, svgProps) => (
  <motion.svg
    {...svgProps}
    animate={controls}
    transition={{ type: "spring", stiffness: 100, damping: 15 }}
    variants={{ normal: { rotate: 0 }, animate: { rotate: 180 } }}
  >
    <path d="M5 12h14" />
    <path d="M12 5v14" />
  </motion.svg>
));
PlusIcon.displayName = "PlusIcon";

/* Pin — pinned-chat mark. Tilts out and drops back in, like a pin being
   pushed into a board. Same silhouette as lucide's Pin. */
export const PinIcon = makeIcon((controls, svgProps) => (
  <motion.svg
    {...svgProps}
    animate={controls}
    transition={{ type: "spring", stiffness: 260, damping: 12 }}
    variants={{ normal: { rotate: 0, y: 0 }, animate: { rotate: [0, -22, 8, 0], y: [0, -1.5, 0.5, 0] } }}
  >
    <path d="M12 17v5" />
    <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
  </motion.svg>
));
PinIcon.displayName = "PinIcon";

/* Agent — a lucid bot mark with a live status spark. The spark gives it a
   quiet "thinking" state at rest; hovering the tab wakes the whole mark up. */
export const AgentIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ onMouseEnter, onMouseLeave, className, size = 14, strokeWidth = 1.75, ...props }, ref) => {
    const controls = useAnimation();
    const isControlledRef = useRef(false);
    const reduceMotion = useReducedMotion();

    const start = useCallback(
      () =>
        reduceMotion
          ? controls.start({ opacity: 1 })
          : controls.start({
              y: [0, -1.25, 0],
              rotate: [0, -5, 5, 0],
              transition: { duration: 0.55, ease: "easeInOut" },
            }),
      [controls, reduceMotion]
    );
    const stop = useCallback(() => controls.start({ y: 0, rotate: 0, opacity: 1 }), [controls]);

    useImperativeHandle(ref, () => {
      isControlledRef.current = true;
      return { startAnimation: start, stopAnimation: stop };
    });

    const enter = useCallback((e: React.MouseEvent<HTMLDivElement>) => (isControlledRef.current ? onMouseEnter?.(e) : start()), [start, onMouseEnter]);
    const leave = useCallback((e: React.MouseEvent<HTMLDivElement>) => (isControlledRef.current ? onMouseLeave?.(e) : stop()), [stop, onMouseLeave]);

    return (
      <div
        className={`relative inline-flex items-center justify-center ${className || ""}`}
        onMouseEnter={enter}
        onMouseLeave={leave}
        style={{ width: size, height: size }}
        {...props}
      >
        <motion.div animate={controls} className="inline-flex" style={{ transformOrigin: "50% 58%" }}>
          <Bot aria-hidden="true" size={size} strokeWidth={strokeWidth} />
        </motion.div>
        <motion.span
          aria-hidden="true"
          className="absolute right-[-1px] top-[-1px] block h-[3px] w-[3px] rounded-full bg-current ring-1 ring-[var(--canvas)]"
          animate={reduceMotion ? { opacity: 1 } : { opacity: [0.35, 1, 0.35], scale: [0.75, 1.15, 0.75] }}
          transition={reduceMotion ? undefined : { duration: 2.2, ease: "easeInOut", repeat: Infinity }}
        />
      </div>
    );
  }
);
AgentIcon.displayName = "AgentIcon";

// Bars erase then redraw one by one — two-phase sequence, so it manages its
// own handle instead of going through makeIcon's single variant switch.
export const ChartColumnIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ onMouseEnter, onMouseLeave, className, size = 14, strokeWidth = 1.75, ...props }, ref) => {
    const controls = useAnimation();
    const isControlledRef = useRef(false);

    const start = useCallback(async () => {
      await controls.start((i) => ({
        pathLength: 0,
        opacity: 0,
        transition: { delay: i * 0.1, duration: 0.3 },
      }));
      await controls.start((i) => ({
        pathLength: 1,
        opacity: 1,
        transition: { delay: i * 0.1, duration: 0.3 },
      }));
    }, [controls]);
    const stop = useCallback(() => controls.start("visible"), [controls]);

    useImperativeHandle(ref, () => {
      isControlledRef.current = true;
      return { startAnimation: start, stopAnimation: stop };
    });

    const enter = useCallback((e: React.MouseEvent<HTMLDivElement>) => (isControlledRef.current ? onMouseEnter?.(e) : start()), [start, onMouseEnter]);
    const leave = useCallback((e: React.MouseEvent<HTMLDivElement>) => (isControlledRef.current ? onMouseLeave?.(e) : stop()), [stop, onMouseLeave]);

    const lineVariants = { visible: { pathLength: 1, opacity: 1 }, hidden: { pathLength: 0, opacity: 0 } };

    return (
      <div
        className={`inline-flex items-center justify-center ${className || ""}`}
        onMouseEnter={enter}
        onMouseLeave={leave}
        {...props}
      >
        <svg
          fill="none"
          height={size}
          width={size}
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={strokeWidth}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <motion.path animate={controls} custom={1} d="M13 17V9" initial="visible" variants={lineVariants} />
          <motion.path animate={controls} custom={2} d="M18 17V5" initial="visible" variants={lineVariants} />
          <path d="M3 3v16a2 2 0 0 0 2 2h16" />
          <motion.path animate={controls} custom={0} d="M8 17v-3" initial="visible" variants={lineVariants} />
        </svg>
      </div>
    );
  }
);
ChartColumnIcon.displayName = "ChartColumnIcon";

const GRID_TRANSITION = { duration: 0.8, ease: "easeInOut", times: [0, 0.4, 0.6, 1] };
const GRID_RECTS = [
  { x: 3, y: 3, tx: [0, 11, 11, 0], ty: [0, 0, 0, 0] },
  { x: 14, y: 3, tx: [0, 0, 0, 0], ty: [0, 11, 11, 0] },
  { x: 14, y: 14, tx: [0, -11, -11, 0], ty: [0, 0, 0, 0] },
  { x: 3, y: 14, tx: [0, 0, 0, 0], ty: [0, -11, -11, 0] },
];

export const LayoutGridIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    {GRID_RECTS.map((r) => (
      <motion.rect
        key={`${r.x}-${r.y}`}
        animate={controls}
        initial="normal"
        height="7"
        width="7"
        rx="1"
        x={r.x}
        y={r.y}
        variants={{
          normal: { translateX: 0, translateY: 0 },
          animate: { translateX: r.tx, translateY: r.ty, transition: GRID_TRANSITION },
        }}
      />
    ))}
  </svg>
));
LayoutGridIcon.displayName = "LayoutGridIcon";

const CALENDAR_DOTS = ["M8 14h.01", "M12 14h.01", "M16 14h.01", "M8 18h.01", "M12 18h.01", "M16 18h.01"];

export const CalendarDaysIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    <path d="M8 2v4" />
    <path d="M16 2v4" />
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M3 10h18" />
    {CALENDAR_DOTS.map((d, i) => (
      <motion.path
        key={d}
        d={d}
        animate={controls}
        custom={i}
        initial="normal"
        strokeWidth={2.5}
        variants={{
          normal: { opacity: 1, transition: { duration: 0.2 } },
          animate: (index) => ({
            opacity: [1, 0, 0, 1],
            transition: { delay: index * 0.1, duration: 1, times: [0, 0.25, 0.55, 1] },
          }),
        }}
      />
    ))}
  </svg>
));
CalendarDaysIcon.displayName = "CalendarDaysIcon";

const WAYPOINT_PATHS = ["m10.2 6.3-3.9 3.9", "M7 12h10", "m13.8 17.7 3.9-3.9"];

const WAYPOINT_CIRCLES = [
  { cx: 12, cy: 4.5 }, { cx: 4.5, cy: 12 }, { cx: 19.5, cy: 12 }, { cx: 12, cy: 19.5 },
];

export const WaypointsIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    {WAYPOINT_CIRCLES.map((c, i) => (
      <motion.circle
        key={`${c.cx}-${c.cy}`}
        cx={c.cx}
        cy={c.cy}
        r="2.5"
        animate={controls}
        custom={i}
        initial="normal"
        variants={{
          normal: { opacity: 1, transition: { duration: 0.2 } },
          animate: (index) => ({
            opacity: [1, 0.3, 1],
            transition: { delay: index * 0.15, duration: 0.7, times: [0, 0.4, 1] },
          }),
        }}
      />
    ))}
    {WAYPOINT_PATHS.map((d, i) => (
      <motion.path
        key={d}
        d={d}
        animate={controls}
        custom={i}
        initial="normal"
        variants={{
          normal: { pathLength: 1, opacity: 1, transition: { duration: 0.2 } },
          animate: (index) => ({
            pathLength: [0, 1],
            opacity: [0, 1],
            transition: { delay: index * 0.2, duration: 0.6 },
          }),
        }}
      />
    ))}
  </svg>
));
WaypointsIcon.displayName = "WaypointsIcon";

export const ArchiveIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    <motion.rect
      x="2"
      y="3"
      width="20"
      height="5"
      rx="1"
      animate={controls}
      initial="normal"
      variants={{
        normal: { translateY: 0, transition: { duration: 0.25 } },
        animate: { translateY: [0, -3, 0], transition: { duration: 0.7, times: [0, 0.35, 1], ease: "easeInOut" } },
      }}
    />
    <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
    <motion.path
      d="M10 12h4"
      animate={controls}
      initial="normal"
      variants={{
        normal: { translateY: 0, transition: { duration: 0.25 } },
        animate: { translateY: [0, 2.5, 0], transition: { delay: 0.08, duration: 0.7, times: [0, 0.35, 1], ease: "easeInOut" } },
      }}
    />
  </svg>
));
ArchiveIcon.displayName = "ArchiveIcon";

// Books slot onto the shelf one by one, the leaning one last.
const LIBRARY_BOOKS = ["M4 4v16", "M8 8v12", "M12 6v14", "m16 6 4 14"];

export const LibraryIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    {LIBRARY_BOOKS.map((d, i) => (
      <motion.path
        key={d}
        d={d}
        animate={controls}
        custom={i}
        initial="normal"
        variants={{
          normal: { opacity: 1, translateY: 0, transition: { duration: 0.2 } },
          animate: (index) => ({
            opacity: [0, 1],
            translateY: [-3, 0],
            transition: { delay: index * 0.1, duration: 0.3, ease: "easeOut" },
          }),
        }}
      />
    ))}
  </svg>
));
LibraryIcon.displayName = "LibraryIcon";

// Checkbox ticks itself, then list lines cascade in.
export const ListTodoIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    <rect x="3" y="5" width="6" height="6" rx="1" />
    <motion.path
      d="m3 17 2 2 4-4"
      animate={controls}
      initial="normal"
      variants={{
        normal: { pathLength: 1, opacity: 1, transition: { duration: 0.2 } },
        animate: {
          pathLength: [0, 1],
          opacity: [0.3, 1],
          transition: { duration: 0.45, ease: "easeInOut" },
        },
      }}
    />
    {["M13 6h8", "M13 12h8", "M13 18h8"].map((d, i) => (
      <motion.path
        key={d}
        d={d}
        animate={controls}
        custom={i}
        initial="normal"
        variants={{
          normal: { opacity: 1, translateX: 0, transition: { duration: 0.2 } },
          animate: (index) => ({
            opacity: [0, 1],
            translateX: [-2, 0],
            transition: { delay: 0.15 + index * 0.1, duration: 0.3 },
          }),
        }}
      />
    ))}
  </svg>
));
ListTodoIcon.displayName = "ListTodoIcon";

// Connector draws from the first node, then the second node pops in.
export const WorkflowIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    <rect x="3" y="3" width="8" height="8" rx="2" />
    <motion.path
      d="M7 11v4a2 2 0 0 0 2 2h4"
      animate={controls}
      initial="normal"
      variants={{
        normal: { pathLength: 1, opacity: 1, transition: { duration: 0.2 } },
        animate: {
          pathLength: [0, 1],
          opacity: [0, 1],
          transition: { duration: 0.4, ease: "easeInOut" },
        },
      }}
    />
    <motion.rect
      x="13"
      y="13"
      width="8"
      height="8"
      rx="2"
      style={{ transformBox: "fill-box", transformOrigin: "center" }}
      animate={controls}
      initial="normal"
      variants={{
        normal: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
        animate: {
          opacity: [0, 1],
          scale: [0.5, 1],
          transition: { delay: 0.3, duration: 0.35, ease: "easeOut" },
        },
      }}
    />
  </svg>
));
WorkflowIcon.displayName = "WorkflowIcon";

// Heartbeat trace: line erases then redraws left-to-right like a live monitor.
export const ActivityIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    <motion.path
      d="M22 12h-4l-3 9L9 3l-3 9H2"
      animate={controls}
      initial="normal"
      variants={{
        normal: { pathLength: 1, opacity: 1, transition: { duration: 0.2 } },
        animate: {
          pathLength: [0, 1],
          opacity: [0.3, 1],
          transition: { duration: 0.8, ease: "easeInOut" },
        },
      }}
    />
  </svg>
));
ActivityIcon.displayName = "ActivityIcon";

const GRIP_CIRCLES = [
  { cx: 19, cy: 5 }, { cx: 19, cy: 12 }, { cx: 12, cy: 5 },
  { cx: 19, cy: 19 }, { cx: 12, cy: 12 }, { cx: 5, cy: 5 },
  { cx: 12, cy: 19 }, { cx: 5, cy: 12 }, { cx: 5, cy: 19 },
];

export const GripIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    {GRIP_CIRCLES.map((c, i) => (
      <motion.circle
        key={`${c.cx}-${c.cy}`}
        animate={controls}
        custom={i}
        initial="normal"
        cx={c.cx}
        cy={c.cy}
        r="1"
        fill="currentColor"
        stroke="none"
        variants={{
          normal: { opacity: 1, transition: { duration: 0.25 } },
          animate: (index) => ({
            opacity: [1, 0.3, 0.3, 1],
            transition: { delay: index * 0.07, duration: 1.1, times: [0, 0.2, 0.8, 1] },
          }),
        }}
      />
    ))}
  </svg>
));
GripIcon.displayName = "GripIcon";

// Text lines rewrite themselves top to bottom.
export const ScrollTextIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    <path d="M19 17V5a2 2 0 0 0-2-2H4" />
    <path d="M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3" />
    {["M15 8h-5", "M15 12h-5"].map((d, i) => (
      <motion.path
        key={d}
        d={d}
        animate={controls}
        custom={i}
        initial="normal"
        variants={{
          normal: { pathLength: 1, opacity: 1, transition: { duration: 0.2 } },
          animate: (index) => ({
            pathLength: [0, 1],
            opacity: [0, 1],
            transition: { delay: index * 0.15, duration: 0.4, ease: "easeOut" },
          }),
        }}
      />
    ))}
  </svg>
));
ScrollTextIcon.displayName = "ScrollTextIcon";

// Bookmark dips down like it's being pressed into place.
export const BookmarkIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    <motion.path
      d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"
      animate={controls}
      initial="normal"
      variants={{
        normal: { translateY: 0, transition: { duration: 0.25 } },
        animate: { translateY: [0, 2.5, 0], transition: { duration: 0.6, times: [0, 0.4, 1], ease: "easeInOut" } },
      }}
    />
  </svg>
));
BookmarkIcon.displayName = "BookmarkIcon";

// Prompt chevron redraws, then the command line types in.
export const SquareTerminalIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <motion.path
      d="m7 11 2-2-2-2"
      animate={controls}
      initial="normal"
      variants={{
        normal: { pathLength: 1, opacity: 1, transition: { duration: 0.2 } },
        animate: { pathLength: [0, 1], opacity: [0.3, 1], transition: { duration: 0.4, ease: "easeInOut" } },
      }}
    />
    <motion.path
      d="M11 13h4"
      animate={controls}
      initial="normal"
      variants={{
        normal: { pathLength: 1, opacity: 1, transition: { duration: 0.2 } },
        animate: { pathLength: [0, 1], opacity: [0, 1], transition: { delay: 0.25, duration: 0.35, ease: "easeOut" } },
      }}
    />
  </svg>
));
SquareTerminalIcon.displayName = "SquareTerminalIcon";

// Whole box does a small settle-wobble.
export const PackageIcon = makeIcon((controls, svgProps) => (
  <motion.svg
    {...svgProps}
    animate={controls}
    initial="normal"
    variants={{
      normal: { rotate: 0, transition: { duration: 0.25 } },
      animate: { rotate: [0, -8, 8, -4, 0], transition: { duration: 0.7, ease: "easeInOut" } },
    }}
  >
    <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z" />
    <path d="M12 22V12" />
    <path d="m3.3 7 7.703 4.734a2 2 0 0 0 1.994 0L20.7 7" />
    <path d="m7.5 4.27 9 5.15" />
  </motion.svg>
));
PackageIcon.displayName = "PackageIcon";

// Big star breathes while the small sparks pop in staggered.
export const SparklesIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    <motion.path
      d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"
      animate={controls}
      initial="normal"
      variants={{
        normal: { scale: 1, transition: { duration: 0.2 } },
        animate: { scale: [1, 0.88, 1.08, 1], transition: { duration: 0.55, ease: "easeInOut" } },
      }}
      style={{ transformOrigin: "12px 12px" }}
    />
    {[
      ["M20 3v4", "M22 5h-4"],
      ["M4 17v2", "M5 18H3"],
    ].map(([a, b], i) => (
      <motion.g
        key={a}
        animate={controls}
        initial="normal"
        variants={{
          normal: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
          animate: {
            opacity: [1, 0, 1],
            scale: [1, 0.4, 1],
            transition: { duration: 0.45, delay: 0.15 + i * 0.15, ease: "easeInOut" },
          },
        }}
        style={{ transformOrigin: i === 0 ? "20px 5px" : "4px 18px" }}
      >
        <path d={a} />
        <path d={b} />
      </motion.g>
    ))}
  </svg>
));
SparklesIcon.displayName = "SparklesIcon";

// Stack fans apart — top layer lifts, bottom layer drops, then settle.
export const LayersIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    <motion.path
      d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z"
      animate={controls}
      initial="normal"
      variants={{
        normal: { y: 0, transition: { duration: 0.2 } },
        animate: { y: [0, -1.6, 0], transition: { duration: 0.55, ease: "easeInOut" } },
      }}
    />
    <path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" />
    <motion.path
      d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65"
      animate={controls}
      initial="normal"
      variants={{
        normal: { y: 0, transition: { duration: 0.2 } },
        animate: { y: [0, 1.6, 0], transition: { duration: 0.55, ease: "easeInOut" } },
      }}
    />
  </svg>
));
LayersIcon.displayName = "LayersIcon";

// Clip swings like it's clipping onto a page.
export const PaperclipIcon = makeIcon((controls, svgProps) => (
  <motion.svg
    {...svgProps}
    animate={controls}
    initial="normal"
    style={{ transformOrigin: "50% 20%" }}
    variants={{
      normal: { rotate: 0, transition: { duration: 0.25 } },
      animate: { rotate: [0, 10, -10, 5, 0], transition: { duration: 0.7, ease: "easeInOut" } },
    }}
  >
    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </motion.svg>
));
PaperclipIcon.displayName = "PaperclipIcon";

// Each knob slides along its track and back.
const SLIDER_ROWS = [
  { track: [["M21 4h-7"], ["M10 4H3"]], knob: "M14 2v4", shift: -4 },
  { track: [["M21 12h-9"], ["M8 12H3"]], knob: "M8 10v4", shift: 4 },
  { track: [["M21 20h-5"], ["M12 20H3"]], knob: "M16 18v4", shift: -4 },
];

export const SlidersHorizontalIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    {SLIDER_ROWS.map((row) => row.track.map(([d]) => <path key={d} d={d} />))}
    {SLIDER_ROWS.map((row, i) => (
      <motion.path
        key={row.knob}
        d={row.knob}
        animate={controls}
        custom={i}
        initial="normal"
        variants={{
          normal: { translateX: 0, transition: { duration: 0.25 } },
          animate: (index) => ({
            translateX: [0, row.shift, 0],
            transition: { delay: index * 0.08, duration: 0.6, times: [0, 0.4, 1], ease: "easeInOut" },
          }),
        }}
      />
    ))}
  </svg>
));
SlidersHorizontalIcon.displayName = "SlidersHorizontalIcon";

// Radio waves ripple outward from the dot — a channel broadcasting.
const RADIO_WAVES: Array<[string, number]> = [
  ["M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5", 0],
  ["M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5", 0],
  ["M4.9 19.1C1 15.2 1 8.8 4.9 4.9", 1],
  ["M19.1 4.9C23 8.8 23 15.2 19.1 19.1", 1],
];

export const RadioIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    <circle cx="12" cy="12" r="2" />
    {RADIO_WAVES.map(([d, ring]) => (
      <motion.path
        key={d}
        d={d}
        animate={controls}
        custom={ring}
        initial="normal"
        variants={{
          normal: { opacity: 1, transition: { duration: 0.2 } },
          animate: (index) => ({
            opacity: [1, 0.2, 1],
            transition: { delay: index * 0.18, duration: 0.7, ease: "easeInOut" },
          }),
        }}
      />
    ))}
  </svg>
));
RadioIcon.displayName = "RadioIcon";

// Rack LEDs blink like a server handshaking.
const SERVER_LEDS = ["M6 6h.01", "M6 18h.01"];

export const ServerIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
    <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
    {SERVER_LEDS.map((d, i) => (
      <motion.path
        key={d}
        d={d}
        strokeWidth={2.5}
        animate={controls}
        custom={i}
        initial="normal"
        variants={{
          normal: { opacity: 1, transition: { duration: 0.2 } },
          animate: (index) => ({
            opacity: [1, 0.2, 1, 0.2, 1],
            transition: { delay: index * 0.25, duration: 0.9, ease: "easeInOut" },
          }),
        }}
      />
    ))}
  </svg>
));
ServerIcon.displayName = "ServerIcon";

// Database — the Spaces tab. Its middle ring draws in, as rows being written.
const DATABASE_RINGS = ["M3 12A9 3 0 0 0 21 12"];
export const DatabaseIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M3 5V19A9 3 0 0 0 21 19V5" />
    {DATABASE_RINGS.map((d) => (
      <motion.path
        key={d}
        d={d}
        animate={controls}
        initial="normal"
        variants={{
          normal: { pathLength: 1, opacity: 1, transition: { duration: 0.2 } },
          animate: { pathLength: [0, 1], opacity: [0, 1], transition: { duration: 0.45, ease: "easeOut" } },
        }}
      />
    ))}
  </svg>
));
DatabaseIcon.displayName = "DatabaseIcon";

// Bell swings from its mount like it's ringing.
/* Messages — the Threads tab. The front bubble lifts, as a new message would. Same
   silhouette as lucide's MessagesSquare. */
export const MessagesSquareIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    <motion.path
      d="M14 9a2 2 0 0 1-2 2H6l-4 4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z"
      animate={controls}
      transition={{ duration: 0.35 }}
      variants={{ normal: { y: 0 }, animate: { y: [0, -1.5, 0] } }}
    />
    <path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1" />
  </svg>
));
MessagesSquareIcon.displayName = "MessagesSquareIcon";

export const BellIcon = makeIcon((controls, svgProps) => (
  <motion.svg
    {...svgProps}
    animate={controls}
    initial="normal"
    style={{ transformOrigin: "top center" }}
    variants={{
      normal: { rotate: 0, transition: { duration: 0.25 } },
      animate: { rotate: [0, -12, 9, -6, 3, 0], transition: { duration: 0.7, ease: "easeInOut" } },
    }}
  >
    <path d="M10.268 21a2 2 0 0 0 3.464 0" />
    <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />
  </motion.svg>
));
BellIcon.displayName = "BellIcon";

// Shackle pops up and settles — a quick unlock-relock.
export const LockIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
    <motion.path
      d="M7 11V7a5 5 0 0 1 10 0v4"
      animate={controls}
      initial="normal"
      variants={{
        normal: { translateY: 0, transition: { duration: 0.25 } },
        animate: { translateY: [0, -1.8, 0], transition: { duration: 0.55, times: [0, 0.4, 1], ease: "easeInOut" } },
      }}
    />
  </svg>
));
LockIcon.displayName = "LockIcon";

// Second teammate leans in and back.
const USERS_SECOND = ["M22 21v-2a4 4 0 0 0-3-3.87", "M16 3.13a4 4 0 0 1 0 7.75"];

export const UsersIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    {USERS_SECOND.map((d) => (
      <motion.path
        key={d}
        d={d}
        animate={controls}
        initial="normal"
        variants={{
          normal: { translateX: 0, opacity: 1, transition: { duration: 0.25 } },
          animate: {
            translateX: [0, -1.5, 0],
            opacity: [1, 0.6, 1],
            transition: { duration: 0.55, times: [0, 0.4, 1], ease: "easeInOut" },
          },
        }}
      />
    ))}
  </svg>
));
UsersIcon.displayName = "UsersIcon";

// Check redraws inside the shield — verification passing.
export const ShieldCheckIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1 1 0 0 1 1.52 0C14.5 3.8 17 5 19 5a1 1 0 0 1 1 1z" />
    <motion.path
      d="m9 12 2 2 4-4"
      animate={controls}
      initial="normal"
      variants={{
        normal: { pathLength: 1, opacity: 1, transition: { duration: 0.2 } },
        animate: { pathLength: [0, 1], opacity: [0.3, 1], transition: { duration: 0.45, ease: "easeOut" } },
      }}
    />
  </svg>
));
ShieldCheckIcon.displayName = "ShieldCheckIcon";

// Key turns in the lock.
export const KeyRoundIcon = makeIcon((controls, svgProps) => (
  <motion.svg
    {...svgProps}
    animate={controls}
    initial="normal"
    style={{ transformOrigin: "70% 30%" }}
    variants={{
      normal: { rotate: 0, transition: { duration: 0.25 } },
      animate: { rotate: [0, -14, 8, 0], transition: { duration: 0.6, ease: "easeInOut" } },
    }}
  >
    <path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z" />
    <circle cx="16.5" cy="7.5" r=".5" fill="currentColor" />
  </motion.svg>
));
KeyRoundIcon.displayName = "KeyRoundIcon";

// Magnetic stripe swipes across the card.
export const CreditCardIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    <rect width="20" height="14" x="2" y="5" rx="2" />
    <motion.path
      d="M2 10h20"
      animate={controls}
      initial="normal"
      variants={{
        normal: { pathLength: 1, opacity: 1, transition: { duration: 0.2 } },
        animate: { pathLength: [0, 1], opacity: [0.3, 1], transition: { duration: 0.5, ease: "easeInOut" } },
      }}
    />
  </svg>
));
CreditCardIcon.displayName = "CreditCardIcon";

// Agent loop: the cycle arrow sweeps once around, the way a turn does.
export const LoopIcon = makeIcon((controls, svgProps) => (
  <svg {...svgProps}>
    <motion.path
      d="M21 12a9 9 0 1 1-2.64-6.36"
      animate={controls}
      initial="normal"
      style={{ transformBox: "fill-box", transformOrigin: "center" }}
      variants={{
        normal: { pathLength: 1, rotate: 0, transition: { duration: 0.2 } },
        animate: {
          pathLength: [0.15, 1],
          rotate: [0, 360],
          transition: { duration: 0.7, ease: "easeInOut" },
        },
      }}
    />
    <motion.path
      d="M21 3v6h-6"
      animate={controls}
      initial="normal"
      variants={{
        normal: { opacity: 1, transition: { duration: 0.2 } },
        animate: { opacity: [0, 1], transition: { delay: 0.4, duration: 0.3 } },
      }}
    />
  </svg>
));
LoopIcon.displayName = "LoopIcon";

/* Close — the × spins a half turn on hover. Mirrors PlusIcon's 180° so the
   open and close affordances read as the same gesture in reverse. */
export const XIcon = makeIcon((controls, svgProps) => (
  <motion.svg
    {...svgProps}
    animate={controls}
    transition={{ type: "spring", stiffness: 100, damping: 15 }}
    variants={{ normal: { rotate: 0 }, animate: { rotate: 180 } }}
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </motion.svg>
));
XIcon.displayName = "XIcon";
